import React from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { EmissionCategory, CATEGORY_COLORS, CATEGORY_DISPLAY_NAMES } from '../../lib/emission-categories';

interface MonthlyEmissionData {
  month: string;
  energy: number;
  transportation: number;
  waste: number;
  total: number;
}

interface EmissionsOverTimeAreaChartProps {
  data: MonthlyEmissionData[];
  selectedYear?: number;
}

export function EmissionsOverTimeAreaChart({ data, selectedYear = new Date().getFullYear() }: EmissionsOverTimeAreaChartProps) {
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-green-200 rounded-lg shadow-lg">
          <p className="font-semibold text-green-800 mb-2">{label} {selectedYear}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey.charAt(0).toUpperCase() + entry.dataKey.slice(1)}: {entry.value.toFixed(2)} MTCO2e
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom legend component
  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex justify-center space-x-6 mt-4">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-green-700 font-medium">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full h-80">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <defs>
            <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CATEGORY_COLORS.energy} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={CATEGORY_COLORS.energy} stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorTransportation" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CATEGORY_COLORS.transportation} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={CATEGORY_COLORS.transportation} stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorWaste" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CATEGORY_COLORS.waste} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={CATEGORY_COLORS.waste} stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="month" 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            label={{ value: 'MTCO2e', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="energy"
            stackId="1"
            stroke={CATEGORY_COLORS.energy}
            fill="url(#colorEnergy)"
            name={CATEGORY_DISPLAY_NAMES.energy}
          />
          <Area
            type="monotone"
            dataKey="transportation"
            stackId="1"
            stroke={CATEGORY_COLORS.transportation}
            fill="url(#colorTransportation)"
            name={CATEGORY_DISPLAY_NAMES.transportation}
          />
          <Area
            type="monotone"
            dataKey="waste"
            stackId="1"
            stroke={CATEGORY_COLORS.waste}
            fill="url(#colorWaste)"
            name={CATEGORY_DISPLAY_NAMES.waste}
          />
          <Legend content={<CustomLegend />} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}













